import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, type ThreadSummary } from "../../lib/api";
import { formatDate, timeAgo } from "../../lib/format";
import { Card, Spinner, useToast } from "../../components/ui";

/**
 * Chat history for the assistant.
 *
 * Threads are grouped by day rather than listed flat. People look for a
 * conversation by when they had it — "the one from Tuesday" — long before they
 * remember what they called it, and an undivided list of forty titles makes
 * that impossible.
 *
 * The search box queries the server rather than filtering the loaded page: the
 * list is capped at fifty threads, so client-side filtering would silently miss
 * anything older than the cap and report "no matches" for a thread that exists.
 */

type Group = { key: string; label: string; threads: ThreadSummary[] };

function startOfDay(value: Date): number {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
}

/** Bucket by calendar day, not by elapsed hours.
 *
 * A question asked at 23:50 last night is "Yesterday" at 00:10 today, even
 * though only twenty minutes have passed. Subtracting milliseconds gets this
 * wrong in exactly the case where the reader is most certain of the answer.
 */
function groupThreads(threads: ThreadSummary[]): Group[] {
  const today = startOfDay(new Date());
  const day = 86_400_000;
  const buckets = new Map<string, Group>();
  const order = ["today", "yesterday", "week", "month", "older"];
  const labels: Record<string, string> = {
    today: "Today",
    yesterday: "Yesterday",
    week: "Previous 7 days",
    month: "Previous 30 days",
    older: "Earlier",
  };

  for (const thread of threads) {
    const stamp = thread.lastMessageAt ?? thread.createdAt;
    const at = startOfDay(new Date(stamp));
    const age = Math.round((today - at) / day);
    const key =
      age <= 0 ? "today" : age === 1 ? "yesterday" : age <= 7 ? "week" : age <= 30 ? "month" : "older";
    const group = buckets.get(key) ?? { key, label: labels[key], threads: [] };
    group.threads.push(thread);
    buckets.set(key, group);
  }

  return order.filter((key) => buckets.has(key)).map((key) => buckets.get(key)!);
}

export function ChatHistory({
  activeId,
  onOpen,
  onNew,
}: {
  activeId: string | null;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const renameRef = useRef<HTMLInputElement>(null);

  // Debounced so typing a five-letter word is one request, not five.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 220);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: threads, isLoading } = useQuery({
    queryKey: ["threads", search],
    queryFn: () =>
      api.get<ThreadSummary[]>("/answers/threads", {
        limit: 50,
        ...(search ? { q: search } : {}),
      }),
  });

  useEffect(() => {
    if (renaming) renameRef.current?.select();
  }, [renaming]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["threads"] });

  const rename = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.patch<ThreadSummary>(`/answers/threads/${id}`, { title }),
    onSuccess: () => {
      setRenaming(null);
      void refresh();
      toast("Thread renamed");
    },
    onError: () => toast("Could not rename the thread"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/answers/threads/${id}`),
    onSuccess: (_result, id) => {
      setConfirmDelete(null);
      void refresh();
      // Deleting the open thread has to clear the transcript too, or the
      // screen keeps showing a conversation that no longer exists.
      if (id === activeId) onNew();
      toast("Thread deleted");
    },
    onError: () => toast("Could not delete the thread"),
  });

  const groups = useMemo(() => groupThreads(threads ?? []), [threads]);
  const total = threads?.length ?? 0;
  const oldest = groups.at(-1)?.threads.at(-1);
  const oldestShown = oldest?.lastMessageAt ?? oldest?.createdAt ?? null;

  return (
    <Card
      collapsible
      storageKey="chat-history"
      title="Chat history"
      meta={total > 0 ? `${total}${total === 50 ? "+" : ""}` : undefined}
      padded={false}
    >
      <div className="flex flex-col gap-2 px-4 pb-1 pt-[13px]">
        <button className="btn-small justify-center" onClick={onNew}>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          New thread
        </button>

        <div className="relative">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#A49DA8"
            strokeWidth="2"
            className="pointer-events-none absolute left-[9px] top-1/2 -translate-y-1/2"
            aria-hidden
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search conversations"
            aria-label="Search conversations"
            className="w-full rounded-field border border-line bg-surface-subtle py-[7px] pl-[27px] pr-7 text-base+ text-ink placeholder:text-muted-soft focus:border-muted-soft focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-[7px] top-1/2 -translate-y-1/2 text-muted-soft hover:text-ink"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="max-h-[340px] overflow-auto px-2 pb-3 pt-1">
        {isLoading && (
          <div className="px-2 py-3">
            <Spinner label="Loading history…" />
          </div>
        )}

        {!isLoading && total === 0 && (
          <p className="px-2 py-3 text-base+ leading-[1.6] text-muted">
            {search
              ? `Nothing matches “${search}”.`
              : "No conversations yet. Ask a question and it will be saved here."}
          </p>
        )}

        {groups.map((group) => (
          <section key={group.key} className="mb-1">
            <div className="px-2 pb-1 pt-2 text-2xs font-extrabold uppercase tracking-[0.12em] text-muted-soft">
              {group.label}
            </div>
            <ul className="flex flex-col gap-px">
              {group.threads.map((thread) => {
                const active = thread.id === activeId;
                const stamp = thread.lastMessageAt ?? thread.createdAt;

                if (renaming === thread.id) {
                  return (
                    <li key={thread.id} className="px-1 py-1">
                      <input
                        ref={renameRef}
                        value={renameDraft}
                        onChange={(event) => setRenameDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && renameDraft.trim()) {
                            rename.mutate({ id: thread.id, title: renameDraft.trim() });
                          }
                          if (event.key === "Escape") setRenaming(null);
                        }}
                        onBlur={() => setRenaming(null)}
                        aria-label="Thread title"
                        className="w-full rounded-nav border border-ink bg-surface px-2 py-1.5 text-base+ font-semibold text-ink focus:outline-none"
                      />
                    </li>
                  );
                }

                return (
                  <li key={thread.id} className="group relative">
                    <button
                      onClick={() => onOpen(thread.id)}
                      title={thread.lastQuestion ?? thread.title}
                      className={`flex w-full items-start gap-[9px] rounded-nav px-2 py-[7px] pr-[52px] text-left transition-colors ${
                        active
                          ? "bg-surface-sunken"
                          : "hover:bg-surface-subtle"
                      }`}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={active ? "#030304" : "#A49DA8"}
                        strokeWidth="1.8"
                        className="mt-[3px] shrink-0"
                        aria-hidden
                      >
                        <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4L3 21l1.1-4.3A8.4 8.4 0 1 1 21 11.5Z" />
                      </svg>
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block truncate text-base+ ${
                            active ? "font-bold text-ink" : "font-semibold text-muted-strong"
                          }`}
                        >
                          {thread.title}
                        </span>
                        {thread.lastQuestion && thread.lastQuestion !== thread.title && (
                          <span className="mt-px block truncate text-sm font-normal text-muted-soft">
                            {thread.lastQuestion}
                          </span>
                        )}
                        <span className="mt-px block text-sm font-normal text-muted-soft">
                          {timeAgo(stamp)} · {thread.messageCount}{" "}
                          {thread.messageCount === 1 ? "message" : "messages"}
                        </span>
                      </span>
                    </button>

                    {/* Row actions. Shown on hover for the mouse, and on focus
                        so they are reachable from the keyboard at all. */}
                    <div className="pointer-events-none absolute right-[6px] top-[6px] flex gap-px opacity-0 transition-opacity focus-within:pointer-events-auto focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100">
                      <button
                        onClick={() => {
                          setRenameDraft(thread.title);
                          setRenaming(thread.id);
                        }}
                        aria-label={`Rename ${thread.title}`}
                        className="rounded-chip p-1 text-muted-soft hover:bg-surface hover:text-ink"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setConfirmDelete(thread.id)}
                        aria-label={`Delete ${thread.title}`}
                        className="rounded-chip p-1 text-muted-soft hover:bg-surface hover:text-danger"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                        </svg>
                      </button>
                    </div>

                    {confirmDelete === thread.id && (
                      <div className="mx-1 mb-1 mt-1 rounded-nav border border-danger-line bg-danger-wash px-2 py-2">
                        <p className="text-sm leading-[1.5] text-danger-deep">
                          Delete this conversation? The answers and their audit
                          record are kept — only the thread disappears.
                        </p>
                        <div className="mt-1.5 flex gap-1.5">
                          <button
                            className="rounded-chip bg-danger px-2 py-1 text-sm font-bold text-white disabled:opacity-60"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(thread.id)}
                          >
                            Delete
                          </button>
                          <button
                            className="rounded-chip px-2 py-1 text-sm font-bold text-muted-strong hover:text-ink"
                            onClick={() => setConfirmDelete(null)}
                          >
                            Keep
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        {total === 50 && !search && (
          <p className="px-2 pt-2 text-sm leading-[1.5] text-muted-soft">
            Showing the fifty most recent. Search to reach older conversations.
          </p>
        )}
      </div>

      {/* The date range is only worth a line when the list spans more than one
          day — otherwise the group heading above already says it. */}
      {groups.length > 1 && (
        <div className="border-t border-line-soft px-4 py-2 text-sm text-muted-soft">
          Oldest shown {formatDate(oldestShown)}
        </div>
      )}

    </Card>
  );
}
